/**
 * COMMS-14: the sync engine already emits a correct sync:status on every
 * cycle outcome, but nothing ever surfaced it — a workspace could go
 * silently offline/unauthenticated for days with zero visible indication.
 * This is the minimum-viable surface: a titlebar icon that only appears once
 * a scope has been offline/auth_error for PROBLEM_DEBOUNCE_MS (so an
 * ordinary few-seconds network blip never flashes it), with a dropdown
 * listing which scope(s) are affected and the last error for each.
 *
 * COMMS-12: also surfaces a server-reported clock-skew warning
 * (syncStatusStore's `clockSkew`) in the same dropdown — previously computed
 * server-side and sent on every affected ack but read by no client, so the
 * loss it warns about (a legitimately-losing LWW edit, silently) stayed
 * exactly as invisible as before that fix.
 */
import React, { useState, useRef, useEffect } from 'react';
import { CloudAlert, CloudOff } from 'lucide-react';
import { useSyncStatusStore } from '../../store/syncStatusStore';
import { useWorkspaceStore } from '../../store/workspaceStore';
import { setUpgradeModalOpen, setUpgradeReason } from '../../store/uiModalStore';
import { useAuthStore } from '../../store/authStore';
import { usePlanLimitStore } from '../../store/planLimitStore';
import { isLimitedPlan } from '../../services/personalSyncConsent';
import { limitRows, limitsStatus } from '../../services/planUsage';
import PlanLimitsPanel from './PlanLimitsPanel';
import { useLocalCapRows } from '../plans/useLocalCaps';

function useDark() {
  const [isDark, setIsDark] = useState(() => document.documentElement.classList.contains('dark'));
  useEffect(() => {
    const obs = new MutationObserver(() => setIsDark(document.documentElement.classList.contains('dark')));
    obs.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });
    return () => obs.disconnect();
  }, []);
  return isDark;
}

// A raw scope id ("ws-123", "personal:42", "team:9", "vault:42") isn't
// meaningful to a user — resolve it to the same names the rest of the app
// uses where possible, and fall back to a generic label otherwise.
function scopeLabel(workspaceId, workspaces) {
  if (workspaceId.startsWith('personal:')) return 'Personal sync (notes & boards)';
  if (workspaceId.startsWith('team:')) return 'Team boards';
  if (workspaceId.startsWith('vault:')) return 'Vault';
  const ws = workspaces.find((w) => String(w.id) === String(workspaceId));
  return ws?.name || `Workspace ${workspaceId}`;
}

function stateLabel(state) {
  return state === 'auth_error' ? 'Needs re-authentication' : 'Offline';
}

// A free-plan account isn't broken — the server declines cloud sync by design
// (402 with `upgrade_required` in the body, see RequireCloudSync +
// check_insert_allowed). Rendering that as "Offline" + a raw JSON blob made an
// expected state look like an outage; recognized here so it can be explained
// instead. Matched on the machine-readable flag, not the prose, so backend
// copy edits don't silently bring the raw error back.
function isPlanGated(entry) {
  return typeof entry?.lastError === 'string' && entry.lastError.includes('upgrade_required');
}

// clockSkewMs is always positive (absolute difference — see sync.ex's
// clock_skew_ms/2) but we don't know the sign (ahead or behind), so this
// stays direction-neutral rather than guessing.
function clockSkewLabel(clockSkewMs) {
  const minutes = Math.round(clockSkewMs / 60000);
  if (minutes < 1) return 'Your clock looks off by under a minute';
  return `Your clock looks off by about ${minutes} minute${minutes === 1 ? '' : 's'}`;
}

const SyncStatusIndicator = () => {
  const isDark = useDark();
  const problems = useSyncStatusStore((s) => s.problems);
  const clockSkew = useSyncStatusStore((s) => s.clockSkew);
  const workspaces = useWorkspaceStore((s) => s.workspaces);
  const user = useAuthStore((s) => s.user);
  const fetchUsage = useAuthStore((s) => s.fetchUsage);
  const lastRefusal = usePlanLimitStore((s) => s.lastRefusal);
  // A new item that landed on this device is the other reason the panel has
  // something to say — without this the one-time explanation is queued into a
  // panel that never renders.
  const deviceNotice = usePlanLimitStore((s) => s.deviceNotice);
  const [showDropdown, setShowDropdown] = useState(false);
  const ref = useRef(null);

  const entries = Object.entries(problems);
  const skewEntries = Object.entries(clockSkew);

  // Plan limits (owner request 15.09.2026): a Free account near or at a limit,
  // or one the server just refused, gets the yellow cloud and the progress bars.
  const limited = isLimitedPlan(user);
  const usage = user?._usage;
  const status = limited ? limitsStatus(usage) : 'ok';
  const limitsAttention = limited && (status !== 'ok' || lastRefusal != null || deviceNotice != null);
  const hasLimitRows = limited && limitRows(usage).length > 0;
  // Plans v2 card 3.3: a local cap reached (or passed, e.g. 3 / 1 boards after
  // landing on Free) shows the panel too; nothing over the cap is touched.
  const localRows = useLocalCapRows();
  const localFull = localRows.some((r) => r.full);

  // Usage is read when the account/plan is known and again after a refusal.
  useEffect(() => {
    if (limited && user?.id != null) fetchUsage?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [limited, user?.id, lastRefusal?.at, deviceNotice?.at]);

  useEffect(() => {
    if (!showDropdown) return undefined;
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setShowDropdown(false);
    };
    document.addEventListener('click', handler);
    return () => document.removeEventListener('click', handler);
  }, [showDropdown]);

  // Close when another titlebar dropdown (notifications, etc.) opens.
  useEffect(() => {
    const handler = (e) => { if (e.detail !== 'sync-status') setShowDropdown(false); };
    window.addEventListener('titlebar-dropdown-open', handler);
    return () => window.removeEventListener('titlebar-dropdown-open', handler);
  }, []);

  if (entries.length === 0 && skewEntries.length === 0 && !limitsAttention && !localFull) return null;

  const totalCount = entries.length + skewEntries.length;
  const onlyLimits = totalCount === 0;

  // When every listed scope is only plan-gated (and nothing else is wrong),
  // the icon drops its warning colours: an expected free-plan state should
  // read as information, not as trouble.
  const allPlanGated = !onlyLimits && skewEntries.length === 0 && entries.every(([, e]) => isPlanGated(e));

  const handleToggle = (e) => {
    e.stopPropagation();
    const opening = !showDropdown;
    if (opening) {
      window.dispatchEvent(new CustomEvent('titlebar-dropdown-open', { detail: 'sync-status' }));
    }
    setShowDropdown(opening);
  };

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={handleToggle}
        className={
          allPlanGated
            ? 'relative px-1 bg-transparent text-gray-400 hover:bg-gray-100 dark:text-gray-500 dark:hover:bg-gray-800/50 rounded-lg transition-colors cursor-pointer'
            : 'relative px-1 bg-transparent text-amber-600 hover:bg-amber-50 dark:text-amber-400 dark:hover:bg-amber-950/30 rounded-lg transition-colors cursor-pointer'
        }
        style={{ WebkitAppRegion: 'no-drag' }}
        aria-label={onlyLimits ? 'Plan limits' : allPlanGated ? 'Cloud sync' : 'Sync problem'}
        title={
          onlyLimits
            ? status === 'full' || lastRefusal
              ? 'A cloud limit of your plan is reached'
              : 'Close to a cloud limit of your plan'
            : allPlanGated
            ? 'Cloud sync is available on paid plans'
            : `Sync trouble: ${totalCount} scope${totalCount > 1 ? 's' : ''} affected`
        }
      >
        {onlyLimits ? <CloudAlert size={16} /> : <CloudOff size={16} />}
        {!allPlanGated && (status === 'full' || lastRefusal || !onlyLimits) && (
          <span
            className="absolute bg-amber-500"
            style={{ top: 4, right: 4, width: 6, height: 6, borderRadius: 9999 }}
            aria-hidden
          />
        )}
      </button>

      {showDropdown && (
        <div
          className="absolute z-50"
          style={{
            right: 0,
            top: 'calc(100% + 8px)',
            width: 300,
            borderRadius: 12,
            overflow: 'hidden',
            background: isDark ? '#161618' : '#fff',
            border: `1px solid ${isDark ? '#2e2e33' : '#e5e7eb'}`,
            boxShadow: '0 8px 32px rgba(0,0,0,0.18)',
          }}
        >
          <div
            style={{
              padding: '8px 14px',
              borderBottom: `1px solid ${isDark ? '#2e2e33' : '#e5e7eb'}`,
              fontSize: 11,
              fontWeight: 600,
              color: 'var(--text)',
              marginTop: 4,
            }}
          >
            {onlyLimits || allPlanGated ? 'Cloud sync' : 'Sync trouble'}
          </div>
          <div style={{ maxHeight: 420, overflowY: 'auto' }}>
            {hasLimitRows || lastRefusal || deviceNotice || localFull ? (
              <PlanLimitsPanel usage={usage} isDark={isDark} localRows={localRows} onClose={() => setShowDropdown(false)} />
            ) : null}
            {entries.map(([workspaceId, entry]) =>
              isPlanGated(entry) ? (
                <div
                  key={workspaceId}
                  style={{
                    padding: '8px 14px',
                    borderBottom: `1px solid ${isDark ? '#2e2e33' : '#f3f4f6'}`,
                  }}
                >
                  <div style={{ fontSize: 11, fontWeight: 600, color: isDark ? '#fff' : '#111827' }}>
                    {scopeLabel(workspaceId, workspaces)}
                  </div>
                  <div
                    style={{
                      fontSize: 10,
                      color: isDark ? '#a1a1aa' : '#6b7280',
                      marginTop: 2,
                    }}
                  >
                    Everything here is saved on this device — nothing is lost. Cloud sync across
                    your devices is part of the paid plans.
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      setUpgradeReason('Sync your work to the cloud and across your devices.');
                      setUpgradeModalOpen(true);
                      setShowDropdown(false);
                    }}
                    style={{
                      marginTop: 6,
                      fontSize: 10,
                      fontWeight: 600,
                      padding: '3px 10px',
                      borderRadius: 6,
                      border: 'none',
                      cursor: 'pointer',
                      background: isDark ? '#4f46e5' : '#6366f1',
                      color: '#fff',
                    }}
                  >
                    See plans
                  </button>
                </div>
              ) : (
                <div
                  key={workspaceId}
                  style={{
                    padding: '8px 14px',
                    borderBottom: `1px solid ${isDark ? '#2e2e33' : '#f3f4f6'}`,
                  }}
                >
                  <div style={{ fontSize: 11, fontWeight: 600, color: isDark ? '#fff' : '#111827' }}>
                    {scopeLabel(workspaceId, workspaces)}
                  </div>
                  <div style={{ fontSize: 10, color: '#d97706', marginTop: 2 }}>
                    {stateLabel(entry.state)}
                  </div>
                  {entry.lastError && (
                    <div
                      style={{
                        fontSize: 10,
                        color: isDark ? '#a1a1aa' : '#6b7280',
                        marginTop: 2,
                        wordBreak: 'break-word',
                      }}
                    >
                      {entry.lastError}
                    </div>
                  )}
                </div>
              ),
            )}
            {skewEntries.map(([workspaceId, entry]) => (
              <div
                key={`skew-${workspaceId}`}
                style={{
                  padding: '8px 14px',
                  borderBottom: `1px solid ${isDark ? '#2e2e33' : '#f3f4f6'}`,
                }}
              >
                <div style={{ fontSize: 11, fontWeight: 600, color: isDark ? '#fff' : '#111827' }}>
                  {scopeLabel(workspaceId, workspaces)}
                </div>
                <div style={{ fontSize: 10, color: '#d97706', marginTop: 2 }}>
                  {clockSkewLabel(entry.clockSkewMs)}
                </div>
                <div
                  style={{
                    fontSize: 10,
                    color: isDark ? '#a1a1aa' : '#6b7280',
                    marginTop: 2,
                  }}
                >
                  A wrong clock can make your edits silently lose to other devices. Check your system time.
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default SyncStatusIndicator;
